import { ColumnType } from '../types';
import { stateKeyOf } from './columns';

/**
 * Merge one level's config order with the user's saved order for that level.
 *
 * - Keys the user ordered that still exist and are `reorderable` keep the user's
 *   relative order.
 * - Every other key — new columns, and `reorderable: false` columns (re-anchored)
 *   — is inserted at its config-neighbor position, so a new column appears where
 *   the config author placed it and is never dumped at the end.
 * - Stale user keys (absent from config) are dropped here; they remain in the
 *   persisted state and are logged separately.
 *
 * Columns sharing a `settingsKey` move together as one unit and stay adjacent.
 */
export function mergeLevelOrder<R>(
  configCols: ColumnType<R>[],
  userOrder: string[] | undefined,
): ColumnType<R>[] {
  const unitsByKey = new Map<string, ColumnType<R>[]>();
  const configKeys: string[] = [];
  for (const col of configCols) {
    const key = stateKeyOf(col);
    const unit = unitsByKey.get(key);
    if (unit) {
      unit.push(col);
    } else {
      unitsByKey.set(key, [col]);
      configKeys.push(key);
    }
  }

  const canReorder = (key: string): boolean => unitsByKey.get(key)?.[0]?.reorderable !== false;

  const orderedKeys: string[] = [];
  const placed = new Set<string>();
  for (const key of userOrder ?? []) {
    if (!placed.has(key) && unitsByKey.has(key) && canReorder(key)) {
      placed.add(key);
      orderedKeys.push(key);
    }
  }
  for (const key of configKeys) {
    if (placed.has(key)) continue;
    placed.add(key);
    insertByConfigNeighbor(orderedKeys, key, configKeys);
  }

  return orderedKeys.flatMap((key) => unitsByKey.get(key) ?? []);
}

/**
 * Splice `key` into `target` next to its nearest config neighbor already placed:
 * after the closest preceding one, else before the closest following one, else
 * at the end. Called in config order so runs of new keys keep their order.
 */
function insertByConfigNeighbor(target: string[], key: string, configKeys: string[]): void {
  const configIndex = configKeys.indexOf(key);
  for (let i = configIndex - 1; i >= 0; i -= 1) {
    const at = target.indexOf(configKeys[i]);
    if (at !== -1) {
      target.splice(at + 1, 0, key);
      return;
    }
  }
  for (let i = configIndex + 1; i < configKeys.length; i += 1) {
    const at = target.indexOf(configKeys[i]);
    if (at !== -1) {
      target.splice(at, 0, key);
      return;
    }
  }
  target.push(key);
}
